/* Reine Logik der Aufgaben: Begriffe, Sortierung, Hinweise. Ohne React, mit Vitest getestet. Sprint und Archiv: sprintlogik.ts */
import { tagMitWochentag } from "../../kern/kalender";
import type { StatusOption } from "../../kern/ui/Statuswahl";

export type Status = "zu_tun" | "in_arbeit" | "wartet" | "erledigt";
export type Prioritaet = "niedrig" | "normal" | "hoch";
/** „vorhaben“ heißt in der Oberfläche „Kapsel“ (Paket C1): Vorstand und die Personen des Vorhabens. Wählbar erst mit Paket C2. */
export type Sichtbarkeit = "vorstand" | "mitglieder" | "vorhaben";

export interface Aufgabe {
  id: string;
  titel: string;
  beschreibung: string;
  eltern_id: string | null;
  eltern_titel: string | null;
  /** Erste Verantwortliche (früheste, bei Gleichstand nach Vorname). Alle stehen in personen. */
  person: string | null;
  /** Alle Verantwortlichen, gleichberechtigt (Stufe 2). Leer heißt Offen. */
  personen: string[];
  status: Status;
  status_grund: string;
  prioritaet: Prioritaet;
  frist_datum: string | null;
  /** Frist ist nur noch ein Datum (Umbau 12.10.2026) */
  frist: string | null;
  sichtbarkeit: Sichtbarkeit;
  ressort_id: string | null;
  vorhaben_id: string | null;
  links: { label: string; url: string }[];
  herkunft: string;
  erledigt_am: string | null;
  archiviert_am: string | null;
  erstellt_von: string | null;
  erstellt_am: string;
  /** Sprint der Aufgabe, bei Unteraufgaben der Sprint der Aufgabe. Ohne Sprint: Backlog. */
  sprint_id: string | null;
  sprint_name: string | null;
  sprint_status: "laufend" | "abgeschlossen" | null;
  ueberfaellig: boolean;
  unteraufgaben_offen: number;
  unteraufgaben_gesamt: number;
  /** Geltende Frist: Datum, sonst Ende des Sprints (bei Unteraufgaben des Sprints der Aufgabe), im Backlog leer */
  frist_gilt: string | null;
  /** Wer zuletzt geändert hat, bei gelöschten Aufgaben: wer gelöscht hat */
  geaendert_von: string | null;
  /** Zahl der Kommentare (ohne gelöschte) */
  kommentare: number;
}

/** Satzende: Sprintnamen enden schon auf einen Punkt („Sprint 26.10.“), dann kommt keiner dazu. */
export const mitPunkt = (satz: string) => (satz.endsWith(".") ? satz : `${satz}.`);

/** Begriffe laut KONZEPT.md. „Offen“ ist kein Status, sondern „ohne Person“. „Archiv“ gehört den Sprints, Aufgaben werden „gelöscht“ (weich). */
export const STATUS: Record<Status, string> = { zu_tun: "Zu tun", in_arbeit: "In Arbeit", wartet: "Wartet", erledigt: "Erledigt" };
/** Optionen der Statuswahl (BAUPLAN-STATUSWAHL.md, Abschnitt 3) */
export const STATUS_OPTIONEN: readonly StatusOption<Status>[] = [
  { wert: "zu_tun", text: STATUS.zu_tun, erklaerung: "Noch nicht begonnen", ton: "neutral", zeichen: "kreis" },
  { wert: "in_arbeit", text: STATUS.in_arbeit, erklaerung: "Jemand ist dran", ton: "warten", zeichen: "halbkreis" },
  { wert: "wartet", text: STATUS.wartet, erklaerung: "Hängt an jemand anderem", ton: "grau", zeichen: "uhr" },
  { wert: "erledigt", text: STATUS.erledigt, erklaerung: "Fertig", ton: "ok", zeichen: "erledigt" },
];
export const PRIORITAET: Record<Prioritaet, string> = { niedrig: "Niedrig", normal: "Normal", hoch: "Hoch" };
export const SICHTBARKEIT: Record<Sichtbarkeit, string> = { vorstand: "Nur Vorstand", mitglieder: "Alle Mitglieder", vorhaben: "Kapsel" };

/** Ressorts und Vorhaben: Texte, Grenzen wie in der Datenbank (aufgaben_ressorts 80, aufgaben_vorhaben 120 Zeichen). */
export type KatalogArt = "ressort" | "vorhaben";
export const KATALOG: Record<KatalogArt, { einzahl: string; ohne: string; neu: string; nameLabel: string; max: number }> = {
  ressort: { einzahl: "Ressort", ohne: "Ohne Ressort", neu: "Neues Ressort …", nameLabel: "Name des neuen Ressorts", max: 80 },
  vorhaben: { einzahl: "Vorhaben", ohne: "Ohne Vorhaben", neu: "Neues Vorhaben …", nameLabel: "Name des neuen Vorhabens", max: 120 },
};
/** Wert des Eintrags „Neues Ressort …“ bzw. „Neues Vorhaben …“ in der Auswahl, nie eine echte id. */
export const KATALOG_NEU = "__neu__";

/** Name für ein neues Ressort oder Vorhaben prüfen: ohne Leerzeichen am Rand, nicht leer, nicht zu lang. */
export function katalogName(art: KatalogArt, eingabe: string): { ok: true; name: string } | { ok: false; fehler: string } {
  const name = eingabe.trim().replace(/\s+/g, " ");
  if (!name) return { ok: false, fehler: "Bitte gib einen Namen ein." };
  if (name.length > KATALOG[art].max) return { ok: false, fehler: `Höchstens ${KATALOG[art].max} Zeichen.` };
  return { ok: true, name };
}

const PRIO_RANG: Record<Prioritaet, number> = { hoch: 0, normal: 1, niedrig: 2 };

/** Sortierung in Meine Woche: überfällig, dann Hoch, dann nach Frist (ohne Frist zuletzt), dann Titel. */
export function sortieren<T extends Pick<Aufgabe, "ueberfaellig" | "prioritaet" | "frist" | "titel">>(liste: readonly T[]): T[] {
  return [...liste].sort(
    (a, b) =>
      Number(b.ueberfaellig) - Number(a.ueberfaellig) ||
      PRIO_RANG[a.prioritaet] - PRIO_RANG[b.prioritaet] ||
      (a.frist ?? "9999").localeCompare(b.frist ?? "9999") ||
      a.titel.localeCompare(b.titel, "de"),
  );
}

/** Hinweis (keine Sperre) bei mehr als zwei Hoch oder mehr als fünf Aufgaben je Person und Sprint (KONZEPT.md).
 *  Jede Aufgabe zählt bei jeder ihrer Personen. */
export function lastHinweis(
  vorhandene: readonly Pick<Aufgabe, "personen" | "sprint_id" | "prioritaet" | "status" | "archiviert_am">[],
  person: string | null,
  sprintId: string | null,
  prioritaet: Prioritaet,
  name: string,
): string | null {
  if (!person || !sprintId) return null;
  const offen = vorhandene.filter((a) => a.personen.includes(person) && a.sprint_id === sprintId && a.status !== "erledigt" && !a.archiviert_am);
  const hoch = offen.filter((a) => a.prioritaet === "hoch").length + (prioritaet === "hoch" ? 1 : 0);
  if (offen.length + 1 > 5) return `${name} hat dann ${offen.length + 1} Aufgaben in diesem Sprint. Vorschlag: höchstens fünf.`;
  if (hoch > 2) return `${name} hat dann ${hoch} Aufgaben mit Hoch in diesem Sprint. Vorschlag: höchstens zwei.`;
  return null;
}

export interface VorschauEingabe {
  ich: string;
  person: { id: string; vorname: string; vorstand: boolean } | null;
  sichtbarkeit: Sichtbarkeit;
  unteraufgabe: boolean;
  /** Name des Vorhabens bei „Kapsel“ */
  vorhaben?: string | null;
}

/** „Das passiert beim Anlegen“ (Ansicht 02). Nur die App, Mail und Slack kommen mit dem Versand dazu. */
export function anlegenVorschau(e: VorschauEingabe): string[] {
  const zeilen: string[] = [];
  const kapsel = e.vorhaben ? `die Personen von ${e.vorhaben}` : "die Personen des Vorhabens";
  if (!e.person) {
    zeilen.push(
      e.sichtbarkeit === "vorstand"
        ? "Der Vorstand sieht sie unter Offen und bekommt eine Mitteilung."
        : e.sichtbarkeit === "vorhaben"
          ? `Der Vorstand und ${kapsel} sehen sie unter Offen und bekommen eine Mitteilung.`
          : "Alle Mitglieder sehen sie unter Offen und bekommen eine Mitteilung.",
    );
  } else if (e.person.id === e.ich) {
    zeilen.push(`Sie liegt bei dir${e.unteraufgabe ? " und erscheint in deiner Woche" : ""}.`);
  } else {
    zeilen.push(`${e.person.vorname} bekommt eine Mitteilung in der App.`);
    if (e.sichtbarkeit === "vorstand" && !e.person.vorstand) zeilen.push(`${e.person.vorname} ist verantwortlich und sieht diese Aufgabe deshalb.`);
  }
  zeilen.push(
    e.sichtbarkeit === "vorstand"
      ? "Sichtbar nur für den Vorstand und wer sie hat."
      : e.sichtbarkeit === "vorhaben"
        ? `Sichtbar für den Vorstand und ${kapsel}.`
        : "Sichtbar für alle Mitglieder.",
  );
  return zeilen;
}

/** Unteraufgaben aus dem Textfeld in „Neue Aufgabe“: eine pro Zeile, leere Zeilen entfallen, höchstens 200 Zeichen je Titel. */
export function unteraufgabenAusText(text: string): string[] {
  return text
    .split("\n")
    .map((z) => z.trim().replace(/\s+/g, " ").slice(0, 200))
    .filter(Boolean);
}

/** Fortschritt einer Hauptaufgabe: „2 von 5 erledigt“ */
export function fortschritt(a: Pick<Aufgabe, "unteraufgaben_offen" | "unteraufgaben_gesamt">): { text: string; anteil: number } | null {
  if (a.unteraufgaben_gesamt === 0) return null;
  const fertig = a.unteraufgaben_gesamt - a.unteraufgaben_offen;
  return { text: `${fertig} von ${a.unteraufgaben_gesamt} erledigt`, anteil: fertig / a.unteraufgaben_gesamt };
}

/** Suche in Titel und Notiz, ohne Groß und Klein. */
export function passt(a: Pick<Aufgabe, "titel" | "beschreibung">, suche: string): boolean {
  const s = suche.trim().toLocaleLowerCase("de");
  if (!s) return true;
  return a.titel.toLocaleLowerCase("de").includes(s) || a.beschreibung.toLocaleLowerCase("de").includes(s);
}

/** Ist die Person verantwortlich? */
export function istVerantwortlich(a: Pick<Aufgabe, "personen">, person: string): boolean {
  return a.personen.includes(person);
}

/** Darf die Person bearbeiten und den Status ändern? Wie aufgaben_darf_bearbeiten in der Datenbank: dazu die Leitung des Vorhabens bei
 *  allen Aufgaben darin, die sie sieht (Paket C1). */
export function darfBearbeiten(a: Pick<Aufgabe, "personen" | "erstellt_von">, ich: string, alleBearbeiten: boolean, leitet = false): boolean {
  return alleBearbeiten || leitet || a.personen.includes(ich) || a.erstellt_von === ich;
}

/** Warum der Haken vor einer Aufgabe nicht geht, sonst null (BAUPLAN-PAKETE.md, Abschnitt 4.1). */
export function hakenGesperrt(
  a: Pick<Aufgabe, "personen" | "erstellt_von" | "archiviert_am" | "sprint_status"> & Partial<Pick<Aufgabe, "vorhaben_id">>,
  ich: string,
  alleBearbeiten: boolean,
  leitet = false,
): string | null {
  if (a.archiviert_am) return "Gelöscht. Erst wiederherstellen, dann abhaken.";
  if (a.sprint_status === "abgeschlossen") return "Im Archiv ändert sich nichts mehr.";
  if (!darfBearbeiten(a, ich, alleBearbeiten, leitet)) {
    return a.vorhaben_id
      ? "Abhaken dürfen die Verantwortlichen, wer die Aufgabe angelegt hat, die Leitung des Vorhabens und der Vorstand."
      : "Abhaken dürfen die Verantwortlichen, wer die Aufgabe angelegt hat, und der Vorstand.";
  }
  return null;
}

/** Statuszeichen in Listen (BAUPLAN-STATUS-IN-LISTEN.md, Abschnitt 1): warum es nicht geht, dieselbe Prüfung wie beim Haken, aber vom
 *  Status gesprochen statt vom Abhaken. Ohne Grund darf die Person den Status ändern. */
export function statusGesperrt(
  a: Pick<Aufgabe, "personen" | "erstellt_von" | "archiviert_am" | "sprint_status"> & Partial<Pick<Aufgabe, "vorhaben_id">>,
  ich: string,
  alleBearbeiten: boolean,
  leitet = false,
): string | null {
  if (a.archiviert_am) return "Gelöscht. Erst wiederherstellen, dann den Status ändern.";
  if (a.sprint_status === "abgeschlossen") return "Im Archiv ändert sich nichts mehr.";
  if (!darfBearbeiten(a, ich, alleBearbeiten, leitet)) {
    return a.vorhaben_id
      ? "Den Status ändern die Verantwortlichen, wer die Aufgabe angelegt hat, die Leitung des Vorhabens und der Vorstand."
      : "Den Status ändern die Verantwortlichen, wer die Aufgabe angelegt hat, und der Vorstand.";
  }
  return null;
}

/** Satzteile zu „bearbeitet“ aus den Werten im Ereignis (Migration aufgaben_kommentare), ohne „hat“. */
function bearbeitetTeile(daten: Record<string, unknown>): string[] {
  const felder = Array.isArray(daten.felder) ? (daten.felder as unknown[]).filter((f): f is string => typeof f === "string") : [];
  const werte = daten.werte && typeof daten.werte === "object" ? (daten.werte as Record<string, unknown>) : {};
  const w = (k: string) => (typeof werte[k] === "string" ? (werte[k] as string) : null);
  const mitWert = (k: string) => k in werte;
  const teile: string[] = [];
  for (const f of felder) {
    if (f === "titel") teile.push("den Titel geändert");
    else if (f === "beschreibung") teile.push("die Beschreibung geändert");
    else if (f === "links") teile.push("die Links geändert");
    else if (f === "frist_datum") {
      const frist = w("frist_datum");
      teile.push(!mitWert("frist_datum") ? "die Frist geändert" : frist ? `die Frist auf ${tagMitWochentag(frist)} gesetzt` : "die Frist entfernt");
    }
    else if (f === "prioritaet") teile.push(w("prioritaet") ? `die Priorität auf ${PRIORITAET[w("prioritaet") as Prioritaet] ?? w("prioritaet")} gesetzt` : "die Priorität geändert");
    else if (f === "ressort_id") teile.push(!mitWert("ressort") ? "das Ressort geändert" : w("ressort") ? `das Ressort auf ${w("ressort")} gesetzt` : "das Ressort entfernt");
    else if (f === "vorhaben_id") teile.push(!mitWert("vorhaben") ? "das Vorhaben geändert" : w("vorhaben") ? `das Vorhaben auf ${w("vorhaben")} gesetzt` : "das Vorhaben entfernt");
    else if (f === "sichtbarkeit") teile.push(w("sichtbarkeit") ? `die Sichtbarkeit auf ${SICHTBARKEIT[w("sichtbarkeit") as Sichtbarkeit] ?? w("sichtbarkeit")} gesetzt` : "die Sichtbarkeit geändert");
  }
  return teile;
}

/** „a, b und c“ */
function aufzaehlen(teile: readonly string[]): string {
  if (teile.length <= 1) return teile[0] ?? "";
  return `${teile.slice(0, -1).join(", ")} und ${teile.at(-1)}`;
}

/** Verlaufseintrag in Worten. werId: wer gehandelt hat (für „hat sich hinzugefügt“). */
export function verlaufText(typ: string, daten: Record<string, unknown>, wer: string, name: (id: string) => string, werId?: string | null): string {
  const s = (k: string) => (typeof daten[k] === "string" ? (daten[k] as string) : "");
  switch (typ) {
    case "aufgaben.person_hinzugefuegt":
      return s("person") && s("person") === werId ? `${wer} hat sich hinzugefügt.` : `${wer} hat ${name(s("person"))} hinzugefügt.`;
    case "aufgaben.person_entfernt":
      return s("person") && s("person") === werId ? `${wer} hat sich entfernt.` : `${wer} hat ${name(s("person"))} entfernt.`;
    case "aufgaben.kommentiert":
      return `${wer} hat kommentiert.`;
    case "aufgaben.kommentar_entfernt":
      return `${wer} hat den Kommentar entfernt.`;
    case "aufgaben.angelegt":
      return `${wer} hat die Aufgabe angelegt${s("person") ? ` für ${name(s("person"))}` : " ohne Person"}.`;
    case "aufgaben.zugewiesen":
      return s("an") ? `${wer} hat sie ${name(s("an"))} gegeben.` : `${wer} hat die Person entfernt. Sie steht unter Offen.`;
    case "aufgaben.uebernommen":
      return `${wer} hat die Aufgabe übernommen.`;
    case "aufgaben.abgegeben":
      return `${wer} hat die Aufgabe abgegeben.`;
    case "aufgaben.erledigt":
      return `${wer} hat sie erledigt.${s("grund") ? ` ${s("grund")}` : ""}`;
    case "aufgaben.status_geaendert": {
      const st = s("status") as Status;
      return `${wer} hat den Status auf ${STATUS[st] ?? st} gesetzt.${s("grund") ? ` Warum: ${s("grund")}` : ""}`;
    }
    case "aufgaben.bearbeitet": {
      const teile = bearbeitetTeile(daten);
      return teile.length ? `${wer} hat ${aufzaehlen(teile)}.` : `${wer} hat die Aufgabe bearbeitet.`;
    }
    case "aufgaben.archiviert":
      return `${wer} hat die Aufgabe gelöscht.`;
    case "aufgaben.wiederhergestellt":
      return `${wer} hat die Aufgabe wiederhergestellt.`;
    case "aufgaben.importiert":
      return "Aus der Slack-Liste übernommen.";
    case "aufgaben.import_zugewiesen":
      return `Laut Slack-Liste ${name(s("an"))} zugewiesen.`;
    case "aufgaben.in_sprint":
      return `${wer} hat sie in ${s("name") || "den Sprint"} geholt.`;
    case "aufgaben.in_backlog":
      return `${wer} hat sie in den Backlog gelegt.`;
    case "aufgaben.wieder_geoeffnet":
      return daten.aus_archiv === true ? `${wer} hat sie wieder geöffnet. Sie liegt im Backlog.` : `${wer} hat sie wieder geöffnet.`;
    default:
      return `${wer}: ${typ}`;
  }
}

export interface Erwaehnbar {
  id: string;
  vorname: string;
}

/** Erwähnungen im Kommentar: alle, deren „@Vorname“ im Text steht. Aus der @Liste gewählte zählen nur, solange ihr Name noch dasteht.
 *  Gibt es einen Vornamen doppelt, zählt nur, wer über die Liste gewählt wurde. */
export function erwaehnteIds(text: string, kandidaten: readonly Erwaehnbar[], gewaehlt: readonly string[] = []): string[] {
  const steht = (vorname: string) => new RegExp(`(^|[^\\p{L}\\p{N}])@${vorname.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\p{L}\\p{N}])`, "iu").test(text);
  const ids: string[] = [];
  for (const k of kandidaten) {
    if (!k.vorname || !steht(k.vorname)) continue;
    const gleichnamig = kandidaten.filter((x) => x.vorname.toLocaleLowerCase("de") === k.vorname.toLocaleLowerCase("de"));
    if (gleichnamig.length > 1 && !gewaehlt.includes(k.id)) continue;
    ids.push(k.id);
  }
  return ids;
}

/** Text in Teile zerlegen, @Namen hervorheben (nur Namen von Personen, damit E-Mail-Adressen normal bleiben). */
export function textMitErwaehnungen(text: string, vornamen: readonly string[]): { text: string; erwaehnt: boolean }[] {
  const namen = [...new Set(vornamen.filter(Boolean))].sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (namen.length === 0) return [{ text, erwaehnt: false }];
  const muster = new RegExp(`@(?:${namen.join("|")})(?![\\p{L}\\p{N}])`, "giu");
  const teile: { text: string; erwaehnt: boolean }[] = [];
  let pos = 0;
  for (const m of text.matchAll(muster)) {
    const i = m.index ?? 0;
    if (i > 0 && /[\p{L}\p{N}]/u.test(text[i - 1] ?? "")) continue;
    if (i > pos) teile.push({ text: text.slice(pos, i), erwaehnt: false });
    teile.push({ text: m[0], erwaehnt: true });
    pos = i + m[0].length;
  }
  if (pos < text.length) teile.push({ text: text.slice(pos), erwaehnt: false });
  return teile;
}

/** Laufende Erwähnung am Cursor: „Schau mal @Ti|“ liefert { start, suche: "Ti" }. */
export function erwaehnungAmCursor(text: string, cursor: number): { start: number; suche: string } | null {
  const vor = text.slice(0, cursor);
  const m = /(^|\s)@([\p{L}\p{N}-]{0,30})$/u.exec(vor);
  if (!m) return null;
  const suche = m[2] ?? "";
  return { start: cursor - suche.length - 1, suche };
}
